import type { DocumentActivityEvent } from "@magic-md/engine-client/protocol";
import { useT } from "@magic-md/i18n/react";
import { ChevronRight, Copy } from "lucide-react";
import { memo, type MouseEvent, useCallback, useMemo, useState } from "react";
import {
    buildPathBreadcrumbSegments,
    condensePathBreadcrumbSegments,
    type PathBreadcrumbSegment,
} from "../lib/path-utils";
import { cn } from "../lib/utils";
import { useEditorStore } from "../store/editor-store";
import { ContextMenu, useContextMenu } from "./ContextMenu";
import { usePaneChromeContext } from "./PaneContext";
import { DocumentActivityIndicator } from "./DocumentActivityBanner";
import { DocumentRunAllControl } from "./DocumentRunAllControl";
import { PanePreviewWidthControl } from "./PanePreviewWidthControl";

const EMPTY_DOCUMENT_ACTIVITIES: DocumentActivityEvent[] = [];

function isNavigableSegment(segment: PathBreadcrumbSegment): boolean {
    return segment.kind === "root" || segment.kind === "folder";
}

export const DocumentPathBreadcrumb = memo(function DocumentPathBreadcrumb() {
    const t = useT("editor");
    const { tabId, paneId, sessionId, splitPercent, filePath, title } =
        usePaneChromeContext();
    const workspaceFolders = useEditorStore((s) => s.workspaceFolders);
    const copyPathToClipboard = useEditorStore((s) => s.copyPathToClipboard);
    const expandTreeNode = useEditorStore((s) => s.expandTreeNode);
    const documentActivities = useEditorStore((s) =>
        sessionId
            ? (s.documentActivities.get(sessionId) ?? EMPTY_DOCUMENT_ACTIVITIES)
            : EMPTY_DOCUMENT_ACTIVITIES,
    );
    const hasDocumentActivity = documentActivities.length > 0;
    const showPreviewControls = splitPercent < 100;
    const contextMenu = useContextMenu();
    const [contextSegment, setContextSegment] =
        useState<PathBreadcrumbSegment | null>(null);

    const segments = useMemo(() => {
        if (!filePath) return [];
        return condensePathBreadcrumbSegments(
            buildPathBreadcrumbSegments(filePath, workspaceFolders),
            6,
        );
    }, [filePath, workspaceFolders]);

    const focusExplorerPath = useCallback(
        (segment: PathBreadcrumbSegment) => {
            if (!isNavigableSegment(segment)) return;
            useEditorStore.setState({ sidebarOpen: true, sidebarView: "explorer" });
            void expandTreeNode(segment.path);
        },
        [expandTreeNode],
    );

    if (!tabId) return null;

    const menuItems = contextSegment
        ? [
            {
                key: "copy-path",
                label: t("menu.copyPath"),
                icon: <Copy size={14} />,
                onClick: () => {
                    void copyPathToClipboard(contextSegment.path);
                },
            },
        ]
        : [];

    const openSegmentMenu = (
        event: MouseEvent,
        segment: PathBreadcrumbSegment,
    ) => {
        if (segment.kind === "ellipsis") return;
        setContextSegment(segment);
        contextMenu.handler(event);
    };

    return (
        <div
            className="flex h-7 shrink-0 items-center gap-1 border-b border-border bg-muted/20 px-2 text-[11px] text-muted-foreground"
            data-testid={`document-path-breadcrumb-${paneId}`}
            aria-label={t("document.path")}
            title={filePath || title}
        >
            <div className="flex min-w-0 shrink items-center gap-0.5 overflow-hidden">
                {hasDocumentActivity ? (
                    <DocumentActivityIndicator
                        activities={documentActivities}
                        className="min-w-0"
                    />
                ) : filePath ? (
                    segments.map((segment, index) => (
                        <span
                            key={`${segment.kind}:${segment.path}:${index}`}
                            className="flex min-w-0 items-center gap-0.5 whitespace-nowrap"
                        >
                            {index > 0 && (
                                <ChevronRight
                                    size={9}
                                    className="shrink-0 text-muted-foreground/40"
                                />
                            )}
                            {isNavigableSegment(segment) ? (
                                <button
                                    type="button"
                                    className="max-w-[140px] truncate rounded px-1 py-0.5 transition-colors hover:bg-muted hover:text-foreground"
                                    title={segment.path}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        focusExplorerPath(segment);
                                    }}
                                    onContextMenu={(event) => openSegmentMenu(event, segment)}
                                >
                                    {segment.label}
                                </button>
                            ) : (
                                <span
                                    className={cn(
                                        "max-w-[180px] truncate px-1 py-0.5",
                                        segment.kind === "file"
                                            ? "font-medium text-foreground"
                                            : "text-muted-foreground/50",
                                    )}
                                    title={segment.path || filePath}
                                    onContextMenu={(event) => openSegmentMenu(event, segment)}
                                >
                                    {segment.label}
                                </span>
                            )}
                        </span>
                    ))
                ) : (
                    <>
                        <span className="truncate font-medium text-foreground">
                            {title.replace(/\.md$/i, "")}
                        </span>
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground/80">
                            {t("document.unsaved")}
                        </span>
                    </>
                )}
            </div>
            <div
                className="ml-3 flex shrink-0 items-center"
                data-testid={`document-path-breadcrumb-controls-${paneId}`}
            >
                <DocumentRunAllControl
                    tabId={tabId}
                    forceVisible
                    showStatusLabel
                />
            </div>
            {showPreviewControls && (
                <div className="ml-auto flex shrink-0 items-center justify-end pl-2">
                    <PanePreviewWidthControl />
                </div>
            )}
            <ContextMenu {...contextMenu.props} items={menuItems} />
        </div>
    );
});
